import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/api/client";
import { useSetupScope } from "@/components/competition/useSetupWrite";
import { useOnline } from "@/hooks/useOnline";
import { useAuthStore } from "@/stores/auth";
import type { WorkspaceProps } from "./model";

/** Cached displays survive refresh/offline; only an exact current read may write. */
export function useRoundWorkspace<T>(
  props: WorkspaceProps,
  objectId: string,
  read: () => Promise<T>,
) {
  const accountId = useAuthStore((state) => state.user?.id);
  const online = useOnline(),
    transport = props.transport ?? api;
  const captureAccount = useSetupScope(props.competitionId, props.competitionVersion);
  const object = JSON.stringify([props.principalKey, props.competitionId, objectId, accountId]);
  const identity = JSON.stringify([
    object,
    props.competitionVersion,
    props.revision,
    [...props.roles].sort(),
    props.rulesAllowed,
    props.rulesEnabled,
    props.manualEnabled,
  ]);
  const latest = useRef({
    identity,
    object,
    read,
    captureAccount,
    transport,
    onSaved: props.onSaved,
  });
  latest.current = { identity, object, read, captureAccount, transport, onSaved: props.onSaved };
  const generation = useRef(0),
    mounted = useRef(true);
  const [snapshot, setSnapshot] = useState<{ identity: string; object: string; data: T } | null>(
    null,
  );
  const [readFailed, setReadFailed] = useState(false);
  const [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const [message, setMessage] = useState("");
  const keys = useRef(new Map<string, string>());
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const reload = useCallback(async () => {
    // A save may refresh parent props before its original callback resumes.
    // Only the current revision may advance the read generation.
    if (!mounted.current || latest.current.identity !== identity) return;
    const request = ++generation.current;
    setLoading(true);
    setError("");
    try {
      const data = await latest.current.read();
      if (
        mounted.current &&
        latest.current.identity === identity &&
        request === generation.current
      ) {
        setSnapshot({ identity, object: latest.current.object, data });
        setReadFailed(false);
      }
    } catch (cause) {
      if (
        !mounted.current ||
        latest.current.identity !== identity ||
        request !== generation.current
      )
        return;
      setReadFailed(true);
      if (cause instanceof ApiError && [401, 403].includes(cause.status)) setSnapshot(null);
      setError(cause instanceof Error ? cause.message : "Could not refresh. Your draft is kept.");
      throw cause;
    } finally {
      if (mounted.current && latest.current.identity === identity && request === generation.current)
        setLoading(false);
    }
  }, [identity]);
  useEffect(() => {
    setBusy(false);
    setMessage("");
    keys.current.clear();
    void reload().catch(() => {});
  }, [reload]);
  useEffect(() => {
    const refresh = () => {
      void reload().catch(() => {});
    };
    window.addEventListener("online", refresh);
    return () => window.removeEventListener("online", refresh);
  }, [reload]);
  const data = snapshot?.object === object ? snapshot.data : null;
  const ready = !!data && snapshot?.identity === identity && !loading && !readFailed && online;
  const stateRef = useRef({ ready });
  stateRef.current = { ready };
  function capture() {
    const account = latest.current.captureAccount(),
      submitted = snapshotRef.current,
      client = transport;
    return () =>
      mounted.current &&
      account() &&
      latest.current.identity === identity &&
      latest.current.transport === client &&
      stateRef.current.ready &&
      navigator.onLine &&
      snapshotRef.current === submitted;
  }
  async function write<R>(
    path: string,
    body: unknown,
    method: "post" | "put" | "delete" = "post",
    extraGuard: () => boolean = () => true,
  ): Promise<R> {
    const current = capture();
    if (!current() || !extraGuard() || busy) throw new Error("Refresh this view before saving.");
    const signature = JSON.stringify([method, path, body]);
    if (!keys.current.has(signature)) keys.current.set(signature, crypto.randomUUID());
    const guard = () => current() && extraGuard();
    const options = {
      canSend: guard,
      canRetry: guard,
      idempotencyKey: keys.current.get(signature),
      onConflict: () => reload().catch(() => {}),
    };
    setBusy(true);
    setError("");
    try {
      let result: R;
      if (method === "delete") {
        if (!transport.delete) throw new Error("This action is unavailable.");
        result = await transport.delete<R>(path, options);
      } else result = await transport[method]<R>(path, body, options);
      if (mounted.current && latest.current.object === object) {
        keys.current.delete(signature);
        setMessage("Saved.");
        await latest.current.onSaved();
        await reload();
      }
      return result;
    } catch (cause) {
      if (mounted.current && latest.current.object === object)
        setError(cause instanceof Error ? cause.message : "Could not save. Your draft is kept.");
      throw cause;
    } finally {
      if (mounted.current && latest.current.object === object) setBusy(false);
    }
  }
  return { data, ready, loading, error, busy, message, online, reload, write, capture, transport };
}
